import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

// Fallback still (shown while video loads / if video can’t play)
import skySrc from '../assets/main/sky.png';

// MP4 background (you said it’s at: src/assets/menu/main_menu.mp4)
import menuVideoSrc from '../assets/menu/main_menu.mp4';

// Main menu UI assets
import logoVideoSrc from '../assets/menu/logo.mp4';
import topLeftOverlaySrc from '../assets/menu/top_left.png';
import topRightOverlaySrc from '../assets/menu/top_right.png';
import mainMenuButtonSrc from '../assets/menu/button_main.png';
import { STARTER_DECK_IDS } from '../combat/config/starterDeck';
import { relicsPool } from '../game/data/relics';
import { TEST_RUN_SPECIAL_BOSS_OPTIONS } from '../game/boss/specialBossFloorConfig';
import { readTestRunConfig, writeTestRunConfig } from '../game/persistence/runStorage';


export type MainMenuParallaxConfig = {
  // Kept for compatibility (in case other parts import/override it),
  // but tower/hero are no longer used since we switched to a video background.
  tower: {
    fit: 'width' | 'height';
    widthVw: number;
    heightVh: number;
    startOffsetPx: number;
    durationMs: number;
    startDelayMs: number;
    panExtraPx: number;
    panMultiplier: number;
    endPaddingPx: number;
  };

  hero: {
    fit: 'width' | 'height';
    widthVw: number;
    heightVh: number;
    startOffsetPx: number;
    endOffsetPx: number;
    startAtTowerProgress: number;
    durationMs: number;
  };

  ui: {
    delayAfterSettleMs: number;
    fadeInMs: number;

    logoWidthPx: number;
    logoWidthVw: number;

    insetRightPx: number;
    insetTopPx: number;
    insetBottomPx: number;

    logoButtonGapPx: number;

    buttonWidthPx: number;
    buttonWidthVw: number;

    buttonRadiusPx: number;
    buttonCropXPx: number;
    buttonNudgeXPx: number;
    buttonNudgeYPx: number;
  };
};

export const MAIN_MENU_PARALLAX_DEFAULTS: MainMenuParallaxConfig = {
  tower: {
    fit: 'width',
    widthVw: 100,
    heightVh: 190,
    startOffsetPx: 100,
    durationMs: 4000,
    startDelayMs: 350,
    panExtraPx: 0,
    panMultiplier: 1,
    endPaddingPx: -200,
  },
  hero: {
    fit: 'width',
    widthVw: 100,
    heightVh: 100,
    startOffsetPx: 1500,
    endOffsetPx: 370,
    startAtTowerProgress: 0.54,
    durationMs: 3500,
  },
  ui: {
    delayAfterSettleMs: 200,
    fadeInMs: 650,

    logoWidthPx: 420,
    logoWidthVw: 42,

    insetRightPx: 28,
    insetTopPx: 18,
    insetBottomPx: 42,

    logoButtonGapPx: 22,

    buttonWidthPx: 260,
    buttonWidthVw: 26,

    buttonRadiusPx: 18,
    buttonCropXPx: 2,
    buttonNudgeXPx: 0,
    buttonNudgeYPx: 2,
  },
};

function mergeCfg(base: MainMenuParallaxConfig, override?: Partial<MainMenuParallaxConfig>): MainMenuParallaxConfig {
  if (!override) return base;
  return {
    tower: Object.assign({}, base.tower, override.tower || {}),
    hero: Object.assign({}, base.hero, override.hero || {}),
    ui: Object.assign({}, base.ui, override.ui || {}),
  };
}

// Small helper: lets us swap layouts on mobile without touching the rest of the screen.
function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const m = window.matchMedia(query);

    const onChange = () => setMatches(m.matches);
    onChange();

    if (m.addEventListener) m.addEventListener('change', onChange);
    else m.addListener(onChange);

    return () => {
      if (m.removeEventListener) m.removeEventListener('change', onChange);
      else m.removeListener(onChange);
    };
  }, [query]);

  return matches;
}

type TestRunConfig = {
  floorLevel: number;
  hp: number;
  sp: number;
  deckIds: string[];
  relicIds: string[];
  floor4BossId: string;
};

type CardOption = {
  id: string;
  title: string;
};

type CachedCardRecord = {
  id?: string;
  _id?: string;
  title?: string;
  name?: string;
  subtitle?: string;
  specialType?: string;
  acquisitionLocked?: boolean;
};

type CardCacheShape = {
  savedAt?: number;
  cards?: CachedCardRecord[];
};

const DEFAULT_TEST_RUN_CONFIG: TestRunConfig = {
  floorLevel: 1,
  hp: 100,
  sp: 100,
  deckIds: [],
  relicIds: [],
  floor4BossId: '',
};

const clampWhole = (value: unknown, fallback: number, min = 0, max = Number.MAX_SAFE_INTEGER) => {
  const parsed = Math.floor(Number(value));
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(min, Math.min(max, parsed));
};

const sanitizeTestRunConfig = (value: unknown): TestRunConfig => {
  const raw = value && typeof value === 'object' ? (value as Partial<TestRunConfig>) : {};
  const deckIds = Array.isArray(raw.deckIds)
    ? raw.deckIds.map((entry) => String(entry ?? '').trim()).filter(Boolean)
    : [];
  const relicIds = Array.isArray(raw.relicIds)
    ? Array.from(new Set(raw.relicIds.map((entry) => String(entry ?? '').trim()).filter(Boolean)))
    : [];
  const allowedBossIds = new Set(
    TEST_RUN_SPECIAL_BOSS_OPTIONS.map((entry) => String(entry.id ?? '').trim())
  );
  const floor4BossId = String(raw.floor4BossId ?? '').trim();

  return {
    floorLevel: clampWhole(raw.floorLevel, DEFAULT_TEST_RUN_CONFIG.floorLevel, 1, 999),
    hp: clampWhole(raw.hp, DEFAULT_TEST_RUN_CONFIG.hp, 1, 999),
    sp: clampWhole(raw.sp, DEFAULT_TEST_RUN_CONFIG.sp, 1, 999),
    deckIds,
    relicIds,
    floor4BossId: allowedBossIds.has(floor4BossId) ? floor4BossId : '',
  };
};

const CARDS_API_URL =
  (typeof process !== 'undefined' && (process as any).env && (process as any).env.REACT_APP_CARDS_API)
    ? (process as any).env.REACT_APP_CARDS_API
    : 'https://card-backend-trwc.onrender.com/api/cards?folder=cards_batch2';

const CARD_CACHE_KEY = `combat_demo_v3_cards_cache_${encodeURIComponent(CARDS_API_URL)}`;

const BUILTIN_PLAYER_CARD_OPTIONS: CardOption[] = [
  { id: 'builtin-lower-the-burden', title: 'Lower the Burden' },
  { id: 'builtin-mend-the-wound', title: 'Mend the Wound' },
  { id: 'builtin-sneak-attack', title: 'Sneak Attack' },
  { id: 'builtin-confidence', title: 'Confidence' },
  { id: 'builtin-overflow-burst', title: 'Overflow Burst' },
  { id: 'builtin-forsaken-tome', title: 'Forsaken Tome' },
  { id: 'builtin-glass-reprisal', title: 'Glass Reprisal' },
  { id: 'builtin-fate-reversed', title: 'Fate Reversed' },
];

const getCardId = (card: CachedCardRecord) => String(card.id ?? card._id ?? '').trim();
const getCardTitle = (card: CachedCardRecord) => String(card.title ?? card.name ?? 'Untitled card').trim();
const getCardSubtitle = (card: CachedCardRecord) => String(card.subtitle ?? '').trim();

const isStoryExclusiveAllyCard = (card: CachedCardRecord) => {
  const subtitle = getCardSubtitle(card).toLowerCase();
  const specialType = String(card.specialType ?? '').trim().toLowerCase();
  if (specialType === 'ally') return true;
  return /(^|\b)ally\s+(attack|defense|utility)(\b|$)/i.test(subtitle);
};

const isSelectablePlayerCard = (card: CachedCardRecord) => {
  const subtitle = getCardSubtitle(card).toLowerCase();
  const specialType = String(card.specialType ?? '').trim().toLowerCase();
  if (!getCardId(card)) return false;
  if (card.acquisitionLocked) return false;
  if (specialType === 'affliction') return false;
  if (subtitle.includes('enemy')) return false;
  if (isStoryExclusiveAllyCard(card)) return false;
  return true;
};

const safeReadCachedCardOptions = (): CardOption[] => {
  if (typeof window === 'undefined') {
    return [...BUILTIN_PLAYER_CARD_OPTIONS].sort((a, b) => a.title.localeCompare(b.title));
  }

  try {
    const raw = window.localStorage.getItem(CARD_CACHE_KEY);
    const parsed = raw ? (JSON.parse(raw) as CardCacheShape) : null;
    const cachedCards = Array.isArray(parsed?.cards) ? parsed.cards : [];

    const options = new Map<string, CardOption>();
    for (const builtin of BUILTIN_PLAYER_CARD_OPTIONS) {
      options.set(builtin.id, builtin);
    }

    for (const card of cachedCards) {
      if (!isSelectablePlayerCard(card)) continue;
      const id = getCardId(card);
      const title = getCardTitle(card);
      if (!id || !title || options.has(id)) continue;
      options.set(id, { id, title });
    }

    return Array.from(options.values()).sort((a, b) => a.title.localeCompare(b.title));
  } catch {
    return [...BUILTIN_PLAYER_CARD_OPTIONS].sort((a, b) => a.title.localeCompare(b.title));
  }
};

export default function MainMenuScreen({
  onNewRun,
  onContinue,
  onOpenLegendarium,
  onStartTestRun,
  canContinue = false,
  config,
}: {
  onNewRun: () => void;
  onContinue?: () => void;
  onOpenLegendarium?: () => void;
  onStartTestRun?: (config: TestRunConfig) => void;
  canContinue?: boolean;
  config?: Partial<MainMenuParallaxConfig>;
}) {

  const reduceMotion = useReducedMotion();
  const cfg = useMemo(() => mergeCfg(MAIN_MENU_PARALLAX_DEFAULTS, config), [config]);

  const isMobile = useMediaQuery('(max-width: 640px)');

  // Background framing tweak for mobile:
  // - smaller Y value shows more of the TOP
  // - larger Y value shows more of the BOTTOM
  const bgObjectPosition = isMobile ? '40% 38%' : '50% 50%';

  // Responsive sizing for the centered menu layout.
  const logoWidth = isMobile ? 'min(78vw, 560px)' : 'min(44vw, 760px)';
  const buttonWidth = isMobile ? 'min(42vw, 250px)' : 'min(15.5vw, 230px)';
  const continueDisabled = !canContinue;
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [testRunConfig, setTestRunConfig] = useState<TestRunConfig>(() =>
    sanitizeTestRunConfig(readTestRunConfig())
  );
  const [cardSearch, setCardSearch] = useState('');
  const [relicSearch, setRelicSearch] = useState('');
  const [cardOptions, setCardOptions] = useState<CardOption[]>(() => safeReadCachedCardOptions());

const [uiReady, setUiReady] = useState(false);
// Menu music + start-gate overlay
const menuAudioRef = useRef<HTMLAudioElement | null>(null);
const [gateOpen, setGateOpen] = useState(false);
const gateRef = useRef<HTMLDivElement | null>(null);

const updateTestRunConfig = (updater: TestRunConfig | ((current: TestRunConfig) => TestRunConfig)) => {
  setTestRunConfig((current) => {
    const next = typeof updater === 'function'
      ? (updater as (current: TestRunConfig) => TestRunConfig)(current)
      : updater;
    return sanitizeTestRunConfig(next);
  });
};

useEffect(() => {
  writeTestRunConfig(testRunConfig);
}, [testRunConfig]);

useEffect(() => {
  if (!isOptionsOpen) return;
  setCardOptions(safeReadCachedCardOptions());
}, [isOptionsOpen]);

useEffect(() => {
  if (!isOptionsOpen) return;

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setIsOptionsOpen(false);
    }
  };

  window.addEventListener('keydown', handleKeyDown);
  return () => window.removeEventListener('keydown', handleKeyDown);
}, [isOptionsOpen]);

const deckCountMap = useMemo(() => {
  const counts = new Map<string, number>();
  for (const id of testRunConfig.deckIds) {
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return counts;
}, [testRunConfig.deckIds]);

const cardNameById = useMemo(() => {
  const map = new Map<string, string>();
  for (const card of cardOptions) {
    map.set(card.id, card.title);
  }
  return map;
}, [cardOptions]);

const selectedDeckSummary = useMemo(() => {
  return Array.from(deckCountMap.entries())
    .map(([id, count]) => ({
      id,
      count,
      title: cardNameById.get(id) || id,
    }))
    .sort((a, b) => a.title.localeCompare(b.title));
}, [deckCountMap, cardNameById]);

const filteredCards = useMemo(() => {
  const needle = cardSearch.trim().toLowerCase();
  if (!needle) return cardOptions;
  return cardOptions.filter((card) => card.title.toLowerCase().includes(needle));
}, [cardOptions, cardSearch]);

const filteredRelics = useMemo(() => {
  const needle = relicSearch.trim().toLowerCase();
  const base = [...relicsPool].sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? '')));
  if (!needle) return base;
  return base.filter((relic) => {
    const hay = `${String(relic.name ?? '')} ${String(relic.description ?? '')} ${String(relic.id ?? '')}`.toLowerCase();
    return hay.includes(needle);
  });
}, [relicSearch]);

const toggleRelicId = (relicId: string) => {
  updateTestRunConfig((current) => {
    const hasRelic = current.relicIds.includes(relicId);
    return {
      ...current,
      relicIds: hasRelic
        ? current.relicIds.filter((id) => id !== relicId)
        : [...current.relicIds, relicId],
    };
  });
};

const changeDeckCount = (cardId: string, delta: number) => {
  if (!cardId || delta === 0) return;
  updateTestRunConfig((current) => {
    const nextDeckIds = [...current.deckIds];
    if (delta > 0) {
      for (let i = 0; i < delta; i += 1) nextDeckIds.push(cardId);
    } else {
      let removals = Math.abs(delta);
      for (let i = nextDeckIds.length - 1; i >= 0 && removals > 0; i -= 1) {
        if (nextDeckIds[i] !== cardId) continue;
        nextDeckIds.splice(i, 1);
        removals -= 1;
      }
    }
    return {
      ...current,
      deckIds: nextDeckIds,
    };
  });
};

const loadStarterDeck = () => {
  updateTestRunConfig((current) => ({
    ...current,
    deckIds: [...STARTER_DECK_IDS],
  }));
};

const handleStartTestRunClick = () => {
  startMenuMusic();
  onStartTestRun?.(sanitizeTestRunConfig(testRunConfig));
};

type DDWindow = Window & { __dd_menuMusic?: HTMLAudioElement | null };

// Derive the app's base path from a bundled asset URL (works for Vite/CRA + subpath deploys).
const deriveBasePrefix = (assetUrl: string) => {
  try {
    const u = new URL(assetUrl, window.location.origin);
    const p = u.pathname;
    const markers = ['/assets/', '/static/', '/build/', '/dist/'];
    for (const m of markers) {
      const idx = p.indexOf(m);
      if (idx >= 0) return p.slice(0, idx);
    }
    return '';
  } catch {
    return '';
  }
};

const ensureMenuAudio = () => {
  if (typeof window === 'undefined') return null;

  const w = window as DDWindow;

  let a =
    (menuAudioRef.current as HTMLAudioElement | null) ??
    ((w.__dd_menuMusic as HTMLAudioElement | null) ?? null);

  if (!a) {
    a = new Audio();
    a.loop = true;
    // Use metadata to avoid eagerly downloading the full track up front.
    a.preload = 'metadata';
    a.volume = 0.0; // fade in when the gate is dismissed
    w.__dd_menuMusic = a;
  }

  const basePrefix = deriveBasePrefix(menuVideoSrc);
  const oggUrl = `${basePrefix}/audio/menu_theme.ogg`;
  const mp3Url = `${basePrefix}/audio/menu_theme.mp3`;

  // Ensure src is set (and update it if you change files/paths).
  const canOgg = a.canPlayType('audio/ogg; codecs="vorbis"') !== '';
  const desiredSrc = canOgg ? oggUrl : mp3Url;
  const desiredAbs = new URL(desiredSrc, window.location.origin).toString();
  if (!a.src || a.src !== desiredAbs) {
    a.src = desiredSrc;
  }

  // Helpful console hint if the file 404s or the codec isn't supported.
  if (!(a as any).__dd_onErrorBound) {
    const onError = () => {
      // eslint-disable-next-line no-console
      console.warn('[MainMenuScreen] Menu music failed to load/play:', a!.src, a!.error);
    };
    a.addEventListener('error', onError);
    (a as any).__dd_onErrorBound = onError;
  }

  menuAudioRef.current = a;
  return a;
};

const fadeVolume = (audio: HTMLAudioElement, target: number, ms: number) => {
  const safeTarget = Math.max(0, Math.min(1, Number.isFinite(target) ? target : 0));
  const safeMs = Math.max(1, Number.isFinite(ms) ? ms : 1);
  const startVol = Math.max(0, Math.min(1, typeof audio.volume === 'number' ? audio.volume : 0));
  const start = performance.now();

  const activeFadeRaf = (audio as any).__dd_menuFadeRaf as number | undefined;
  if (activeFadeRaf) cancelAnimationFrame(activeFadeRaf);

  const tick = (now: number) => {
    const t = Math.max(0, Math.min(1, (now - start) / safeMs));
    const nextVol = startVol + (safeTarget - startVol) * t;

    try {
      audio.volume = Math.max(0, Math.min(1, Number.isFinite(nextVol) ? nextVol : safeTarget));
    } catch {}

    if (t < 1) {
      (audio as any).__dd_menuFadeRaf = requestAnimationFrame(tick);
      return;
    }

    (audio as any).__dd_menuFadeRaf = 0;
  };

  (audio as any).__dd_menuFadeRaf = requestAnimationFrame(tick);
};

const startMenuMusic = () => {
  const a = ensureMenuAudio();
  if (!a) return;

  if (a.paused) a.play().catch(() => {});
  fadeVolume(a, 0.55, 350);
};

const handleGateStart = (e?: React.SyntheticEvent) => {
  if (e) {
    e.preventDefault();
    e.stopPropagation();
  }
  startMenuMusic();
  if (!gateOpen) return;
  setGateOpen(false);
};

useEffect(() => {
  if (!gateOpen) return;
  gateRef.current?.focus();
}, [gateOpen]);

// Prepare the singleton audio early so the first tap always works.
useEffect(() => {
  ensureMenuAudio();
  startMenuMusic();

  return () => {
    // Intentionally do NOT pause/clear the audio here.
    // App.js is responsible for stopping it once the game is fully booted.
    menuAudioRef.current = null;
  };
}, []);

// Only keep the logo/button fade-in timing

  useEffect(() => {
    const delay = reduceMotion ? 0 : Math.max(0, cfg.ui.delayAfterSettleMs);
    const t = window.setTimeout(() => setUiReady(true), delay);
    return () => window.clearTimeout(t);
  }, [reduceMotion, cfg.ui.delayAfterSettleMs]);

  const renderMenuButton = (
    label: string,
    onClick?: () => void,
    disabled = false
  ) => (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled) return;
        startMenuMusic();
        onClick?.();
      }}
      disabled={disabled}
      aria-disabled={disabled}
      className="relative inline-block p-0 m-0 bg-transparent border-0 outline-none pointer-events-auto"
      style={{
        WebkitTapHighlightColor: 'transparent',
        backgroundColor: 'transparent',
        lineHeight: 0,
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      <img
        src={mainMenuButtonSrc}
        alt=""
        draggable={false}
        className="block h-auto select-none pointer-events-none"
        style={{
          width: buttonWidth,
          filter: disabled ? 'grayscale(1) brightness(0.6)' : 'none',
        }}
      />

      <span
        className="absolute inset-0 flex items-center justify-center text-white select-none"
        style={{
          fontFamily: 'Karma, ui-serif, Georgia, serif',
          fontSize: isMobile ? 'clamp(14px, 2.1vw, 19px)' : 'clamp(16px, 1.2vw, 22px)',
          textShadow: '0 2px 6px rgba(0,0,0,0.85)',
          letterSpacing: '0.01em',
          pointerEvents: 'none',
          opacity: disabled ? 0.72 : 1,
          color: disabled ? 'rgba(210,210,210,0.9)' : '#f1ede3',
          transform: 'translateY(2px)',
        }}
      >
        {label}
      </span>
    </button>
  );

  const renderCornerOverlay = (src: string, side: 'left' | 'right') => {
    const overlayWidth = isMobile ? 'min(76vw, 520px)' : 'min(50vw, 720px)';
    const maskPosition = side === 'left' ? 'top left' : 'top right';
    const shimmerTravel = { backgroundPosition: ['50% 135%', '50% -35%'] };

    const maskStyle = {
      WebkitMaskImage: `url(${src})`,
      WebkitMaskRepeat: 'no-repeat',
      WebkitMaskSize: 'contain',
      WebkitMaskPosition: maskPosition,
      maskImage: `url(${src})`,
      maskRepeat: 'no-repeat',
      maskSize: 'contain',
      maskPosition,
    } as React.CSSProperties;

    return (
      <div
        className={`absolute bottom-0 ${side === 'left' ? 'left-0' : 'right-0'} select-none pointer-events-none`}
        style={{
          width: overlayWidth,
          transform: 'scaleY(-1)',
          transformOrigin: 'center center',
        }}
      >
        <img
          src={src}
          alt=""
          aria-hidden
          draggable={false}
          className="block w-full h-auto select-none pointer-events-none"
        />

        {!reduceMotion && (
          <>
            <motion.div
              className="absolute inset-0 pointer-events-none"
              animate={{ opacity: [0.14, 0.26, 0.15] }}
              transition={{ duration: 4.2, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                ...maskStyle,
                background:
                  'linear-gradient(180deg, rgba(255,120,20,0.10) 0%, rgba(255,140,35,0.18) 28%, rgba(255,185,90,0.34) 62%, rgba(255,140,35,0.14) 100%)',
                mixBlendMode: 'screen',
                filter: 'blur(1.2px) saturate(1.3)',
              }}
            />

            <motion.div
              className="absolute inset-0 pointer-events-none"
              animate={{
                ...shimmerTravel,
                opacity: [0, 0.9, 0],
              }}
              transition={{
                duration: 3.6,
                repeat: Infinity,
                ease: 'linear',
                repeatDelay: 0.8,
              }}
              style={{
                ...maskStyle,
                background:
                  'linear-gradient(180deg, transparent 28%, rgba(255,155,45,0) 40%, rgba(255,227,150,1) 50%, rgba(255,155,45,0) 60%, transparent 72%)',
                backgroundSize: '170% 250%',
                mixBlendMode: 'screen',
                filter: 'blur(1.7px) drop-shadow(0 0 8px rgba(255,185,90,0.65))',
              }}
            />

            <motion.div
              className="absolute inset-0 pointer-events-none"
              animate={{ opacity: [0.02, 0.1, 0.03] }}
              transition={{ duration: 5.4, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                ...maskStyle,
                background: 'rgba(255,150,40,0.85)',
                mixBlendMode: 'screen',
                filter: 'blur(8px)',
              }}
            />
          </>
        )}
      </div>
    );
  };

  return (
    <div className="relative w-full min-h-[100svh] overflow-hidden bg-black">
      {/* Fallback still image */}
      <img
        src={skySrc}
        alt=""
        aria-hidden
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
        style={{ objectPosition: bgObjectPosition }}
      />

      {/* MP4 background */}
      <video
        className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
        style={{ objectPosition: bgObjectPosition }}
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        aria-hidden
      >
        <source src={menuVideoSrc} type="video/mp4" />
      </video>

      {/* Subtle vignette / film grain */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(closest-side at 50% 55%, rgba(0,0,0,0) 0%, rgba(0,0,0,0.45) 55%, rgba(0,0,0,0.75) 100%), linear-gradient(rgba(0,0,0,0.12), rgba(0,0,0,0.22))',
          mixBlendMode: 'multiply',
        }}
      />

      {/* UI (fade/slide in only) */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0, y: 10 }}
        animate={uiReady ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
        transition={
          reduceMotion
            ? { duration: 0 }
            : { duration: Math.max(0.05, cfg.ui.fadeInMs / 1000), ease: [0.16, 1, 0.3, 1] }
        }
      >
        {renderCornerOverlay(topLeftOverlaySrc, 'left')}
        {renderCornerOverlay(topRightOverlaySrc, 'right')}

        <div
          className="absolute left-1/2 -translate-x-1/2 select-none pointer-events-none"
          style={{
            top: isMobile ? '6.5vh' : '4vh',
            width: logoWidth,
          }}
        >
          <video
            src={logoVideoSrc}
            aria-label="Emberfade"
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            className="block w-full h-auto pointer-events-none select-none"
          />
        </div>

        <div
          className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center gap-3 sm:gap-4 md:gap-5 flex-wrap"
          style={{
            bottom: isMobile ? '8vh' : '7.5vh',
            width: 'min(92vw, 1000px)',
          }}
        >
          {renderMenuButton('New Reading', onNewRun, false)}
          {renderMenuButton('Continue', onContinue, continueDisabled)}
          {renderMenuButton('Legendarium', onOpenLegendarium, false)}
          {renderMenuButton('Options', () => setIsOptionsOpen(true), false)}
        </div>
      </motion.div>


      {isOptionsOpen && (
        <div
          className="absolute inset-0 z-[950] flex items-center justify-center px-4 py-6"
          onMouseDown={() => setIsOptionsOpen(false)}
          style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(5px)' }}
        >
          <div
            className="w-full max-w-[1200px] max-h-[90vh] overflow-hidden rounded-[22px] border border-white/10 bg-[#120d0b] text-[#f1ede3] shadow-[0_24px_80px_rgba(0,0,0,0.55)]"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b border-white/10 px-5 py-4 sm:px-6">
              <div>
                <div className="font-serif text-[28px] leading-none">Options</div>
                <div className="mt-2 text-sm text-white/70">
                  Test Run lets you choose a starting floor, HP, SP, custom deck, and trinkets.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsOptionsOpen(false)}
                className="rounded-full border border-white/15 px-3 py-1 text-sm text-white/80 transition hover:bg-white/10"
              >
                Close
              </button>
            </div>

            <div className="grid gap-5 overflow-y-auto px-5 py-5 sm:px-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(340px,0.9fr)]" style={{ maxHeight: 'calc(90vh - 86px)' }}>
              <div className="min-w-0 space-y-4">
                <div className="rounded-[18px] border border-white/10 bg-black/20 p-4">
                  <div className="mb-3 font-serif text-[20px]">Test Run setup</div>
                  <div className="grid gap-3 sm:grid-cols-4">
                    <label className="text-sm text-white/75">
                      <div className="mb-1">Starting floor</div>
                      <input
                        type="number"
                        min={1}
                        max={999}
                        value={testRunConfig.floorLevel}
                        onChange={(event) =>
                          updateTestRunConfig((current) => ({
                            ...current,
                            floorLevel: clampWhole(event.target.value, current.floorLevel, 1, 999),
                          }))
                        }
                        className="w-full rounded-[12px] border border-white/10 bg-[#211816] px-3 py-2 text-white outline-none"
                      />
                    </label>
                    <label className="text-sm text-white/75">
                      <div className="mb-1">Starting HP</div>
                      <input
                        type="number"
                        min={1}
                        max={999}
                        value={testRunConfig.hp}
                        onChange={(event) =>
                          updateTestRunConfig((current) => ({
                            ...current,
                            hp: clampWhole(event.target.value, current.hp, 1, 999),
                          }))
                        }
                        className="w-full rounded-[12px] border border-white/10 bg-[#211816] px-3 py-2 text-white outline-none"
                      />
                    </label>
                    <label className="text-sm text-white/75">
                      <div className="mb-1">Starting SP</div>
                      <input
                        type="number"
                        min={1}
                        max={999}
                        value={testRunConfig.sp}
                        onChange={(event) =>
                          updateTestRunConfig((current) => ({
                            ...current,
                            sp: clampWhole(event.target.value, current.sp, 1, 999),
                          }))
                        }
                        className="w-full rounded-[12px] border border-white/10 bg-[#211816] px-3 py-2 text-white outline-none"
                      />
                    </label>
                    <label className="text-sm text-white/75">
                      <div className="mb-1">Floor 4 boss</div>
                      <select
                        value={testRunConfig.floor4BossId}
                        onChange={(event) =>
                          updateTestRunConfig((current) => ({
                            ...current,
                            floor4BossId: String(event.target.value ?? '').trim(),
                          }))
                        }
                        className="w-full rounded-[12px] border border-white/10 bg-[#211816] px-3 py-2 text-white outline-none"
                      >
                        {TEST_RUN_SPECIAL_BOSS_OPTIONS.map((option) => (
                          <option key={option.id || 'random'} value={option.id}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div className="mt-3 text-xs text-white/55">
                    HP and SP above the normal base also raise the run's max values so the start won't be clamped.
                  </div>
                  <div className="mt-2 text-xs text-white/50">
                    Floor 4 boss can stay random or force one boss without editing weights by hand.
                  </div>
                </div>

                <div className="rounded-[18px] border border-white/10 bg-black/20 p-4">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="font-serif text-[20px]">Deck builder</div>
                      <div className="text-xs text-white/55">
                        Add as many copies as you want. Cards are shown here as cached text names only. If you leave the deck empty, the normal starter deck will be used.
                      </div>
                    </div>
                    <div className="text-sm text-white/75">Selected cards: {testRunConfig.deckIds.length}</div>
                  </div>

                  <div className="mb-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={loadStarterDeck}
                      className="rounded-[12px] border border-white/10 bg-white/5 px-3 py-2 text-sm transition hover:bg-white/10"
                    >
                      Load starter deck
                    </button>
                    <button
                      type="button"
                      onClick={() => updateTestRunConfig((current) => ({ ...current, deckIds: [] }))}
                      className="rounded-[12px] border border-white/10 bg-white/5 px-3 py-2 text-sm transition hover:bg-white/10"
                    >
                      Clear deck
                    </button>
                  </div>

                  <input
                    type="text"
                    value={cardSearch}
                    onChange={(event) => setCardSearch(event.target.value)}
                    placeholder="Search cards"
                    className="mb-3 w-full rounded-[12px] border border-white/10 bg-[#211816] px-3 py-2 text-white outline-none"
                  />

                  <div className="max-h-[42vh] overflow-y-auto rounded-[14px] border border-white/10 bg-black/15">
                    {filteredCards.length === 0 && (
                      <div className="px-4 py-4 text-sm text-white/60">No cards match that search.</div>
                    )}
                    {filteredCards.map((card) => {
                      const count = deckCountMap.get(card.id) ?? 0;
                      return (
                        <div
                          key={card.id}
                          className="flex items-center gap-3 border-b border-white/5 px-4 py-3 last:border-b-0"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm text-white">{card.title}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => changeDeckCount(card.id, -1)}
                              className="h-8 w-8 rounded-full border border-white/10 bg-white/5 text-lg leading-none transition hover:bg-white/10"
                            >
                              −
                            </button>
                            <div className="w-8 text-center text-sm text-white/80">{count}</div>
                            <button
                              type="button"
                              onClick={() => changeDeckCount(card.id, 1)}
                              className="h-8 w-8 rounded-full border border-white/10 bg-white/5 text-lg leading-none transition hover:bg-white/10"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="min-w-0 space-y-4">
                <div className="rounded-[18px] border border-white/10 bg-black/20 p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <div className="font-serif text-[20px]">Trinkets</div>
                      <div className="text-xs text-white/55">Pick any number from the full trinket pool.</div>
                    </div>
                    <div className="text-sm text-white/75">Selected: {testRunConfig.relicIds.length}</div>
                  </div>

                  <div className="mb-3 flex gap-2">
                    <button
                      type="button"
                      onClick={() => updateTestRunConfig((current) => ({ ...current, relicIds: [] }))}
                      className="rounded-[12px] border border-white/10 bg-white/5 px-3 py-2 text-sm transition hover:bg-white/10"
                    >
                      Clear trinkets
                    </button>
                  </div>

                  <input
                    type="text"
                    value={relicSearch}
                    onChange={(event) => setRelicSearch(event.target.value)}
                    placeholder="Search trinkets"
                    className="mb-3 w-full rounded-[12px] border border-white/10 bg-[#211816] px-3 py-2 text-white outline-none"
                  />

                  <div className="max-h-[34vh] overflow-y-auto rounded-[14px] border border-white/10 bg-black/15">
                    {filteredRelics.length === 0 && (
                      <div className="px-4 py-4 text-sm text-white/60">No trinkets match that search.</div>
                    )}
                    {filteredRelics.map((relic) => {
                      const checked = testRunConfig.relicIds.includes(relic.id);
                      return (
                        <label
                          key={relic.id}
                          className="flex cursor-pointer items-start gap-3 border-b border-white/5 px-4 py-3 last:border-b-0"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleRelicId(relic.id)}
                            className="mt-1"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm text-white">{relic.name}</div>
                            <div className="text-[11px] uppercase tracking-[0.08em] text-white/45">
                              {relic.rarity || 'trinket'}
                            </div>
                            <div className="mt-1 text-xs text-white/60">{relic.description}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="rounded-[18px] border border-white/10 bg-black/20 p-4">
                  <div className="mb-3 font-serif text-[20px]">Summary</div>
                  <div className="grid gap-2 text-sm text-white/75 sm:grid-cols-3">
                    <div>Floor: <span className="text-white">{testRunConfig.floorLevel}</span></div>
                    <div>HP: <span className="text-white">{testRunConfig.hp}</span></div>
                    <div>SP: <span className="text-white">{testRunConfig.sp}</span></div>
                  </div>
                  <div className="mt-2 text-sm text-white/75">
                    Floor 4 boss:{' '}
                    <span className="text-white">
                      {TEST_RUN_SPECIAL_BOSS_OPTIONS.find((option) => option.id === testRunConfig.floor4BossId)?.name || 'Random (weights)'}
                    </span>
                  </div>
                  <div className="mt-4">
                    <div className="mb-2 text-xs uppercase tracking-[0.08em] text-white/45">Selected deck</div>
                    <div className="max-h-[18vh] overflow-y-auto rounded-[12px] border border-white/10 bg-black/15 px-3 py-2 text-sm text-white/75">
                      {selectedDeckSummary.length === 0 ? (
                        <div className="text-white/55">Starter deck fallback</div>
                      ) : (
                        selectedDeckSummary.map((entry) => (
                          <div key={entry.id} className="flex items-center justify-between gap-3 py-1">
                            <span className="truncate">{entry.title}</span>
                            <span className="text-white/55">x{entry.count}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleStartTestRunClick}
                      className="rounded-[14px] border border-[#a86f3d] bg-[#6e4020] px-4 py-2 text-sm text-[#fff3dd] transition hover:bg-[#814b26]"
                    >
                      Start Test Run
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsOptionsOpen(false)}
                      className="rounded-[14px] border border-white/10 bg-white/5 px-4 py-2 text-sm transition hover:bg-white/10"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

{/* Gate overlay: first interaction starts music + unlocks the menu */}
{gateOpen && (
  <div
    ref={gateRef}
    role="button"
    aria-label="Click or tap to start the Demo"
    className="absolute inset-0 z-[999] flex items-center justify-center"
    tabIndex={0}
    style={{ cursor: 'pointer' }}
    onPointerDown={handleGateStart}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') handleGateStart(e);
    }}
  >
    <div
      style={{
        padding: '14px 18px',
        background: 'rgba(0,0,0,0.35)',
        borderRadius: 12,
        backdropFilter: 'blur(2px)',
      }}
    >
      <div className="font-serif text-white text-[20px] sm:text-[26px]">Click or tap to start the Demo</div>
    </div>
  </div>
)}

    </div>
  );
}
